import {
  FieldFunctionOptions,
  FieldMergeFunction,
  InMemoryCache,
} from '@apollo/client';
import { cacheSizes } from '@apollo/client/utilities';
import {
  QueryAllStreamsPostsSearchArgs,
  QueryEnrichmentInvocationsArgs,
  QueryMySourcesArgs,
  QuerySearchArgs,
  QuerySearchStreamPostsArgs,
  QueryTypeaheadArgs,
  TypeaheadResult,
} from '../__generated__/schema-types';

// Cap Apollo's memoized result cache: with the defaults, subscription churn
// accumulates GBs of stale results. Must run before InMemoryCache is created.
cacheSizes['inMemoryCache.executeSelectionSet'] = 8192; // default 50_000
cacheSizes['inMemoryCache.executeSubSelectedArray'] = 2048; // default 10_000

export type Page = {
  __typename?: string;
  items: Array<unknown>;
  nextToken?: string | null;
};

const mergePaginatedCollections: FieldMergeFunction<
  Page,
  Page,
  FieldFunctionOptions<{ limit?: number; nextToken?: string }>
> = (existingPage: Page = { items: [] }, incomingPage: Page, { args }) => {
  // requesting with nextToken === null most probably means that we want to do fresh refetch
  if (args?.nextToken === null) {
    return incomingPage;
  }

  // apollo can write multiple times the same data. (e.g. if we navigate to page before prefetching is finished)
  // theoretically that's enough to just check nextToken. In case of problems migrate to filtering incoming items from duplicates
  // https://github.com/apollographql/apollo-client/issues/10711
  if (incomingPage.nextToken === existingPage.nextToken) {
    return existingPage;
  }

  return {
    ...incomingPage,
    items: [...existingPage.items, ...incomingPage.items],
  };
};

type CursorPage = {
  __typename?: string;
  items: Array<unknown>;
  nextCursor?: string | null;
};

const mergeCursorPages: FieldMergeFunction<
  CursorPage,
  CursorPage,
  FieldFunctionOptions<{ input?: { cursor?: string | null } }>
> = (existingPage, incomingPage, { args }) => {
  if (!args?.input?.cursor || !existingPage) {
    return incomingPage;
  }

  if (incomingPage.nextCursor === existingPage.nextCursor) {
    return existingPage;
  }

  return {
    ...incomingPage,
    items: [...existingPage.items, ...incomingPage.items],
  };
};

export const makeApolloCache = () =>
  new InMemoryCache({
    typePolicies: {
      VoxHistoryObject: {
        keyFields: false,
      },
      VoxFullAccessRequest: {
        keyFields: false,
      },
      User: {
        fields: {
          posts: {
            keyArgs: false,
            merge: mergePaginatedCollections,
          },
          protectedFields: {
            merge: true,
          },
        },
      },
      RegularPost: {
        fields: {
          media: {
            merge: false,
          },
        },
      },
      S3Object: {
        keyFields: ['key'],
      },
      Tournament: {
        fields: {
          submissions: {
            keyArgs: false,
            merge: mergePaginatedCollections,
          },
          participants: {
            keyArgs: false,
            merge: mergePaginatedCollections,
          },
          medals: {
            merge: false,
          },
        },
      },
      Stream: {
        fields: {
          paidData: {
            merge: false,
          },
        },
      },
      Source: {
        keyFields: ['sourceId'],
        fields: {
          routes: {
            merge: false,
          },
        },
      },
      SourceRoute: {
        keyFields: ['routeId'],
      },
      StreamPackage: {
        fields: {
          // use different field name to avoid conflict with Stream.paidData
          // as for stream `paidData` can be null, but for package not
          packagePaidData: {
            merge: false,
          },
        },
      },

      UserSettings: {
        keyFields: [],
      },

      TerminalSettings: {
        keyFields: [],
      },

      OpticFeedsTwttrQuery: {
        keyFields: [],
      },

      Query: {
        fields: {
          tournaments: {
            keyArgs: ['state', 'category', 'sortBy'],
            merge: mergePaginatedCollections,
          },
          closedTournaments: {
            keyArgs: ['category'],
            merge: mergePaginatedCollections,
          },
          tournament: {
            read(_, { args, toReference }) {
              return toReference({
                __typename: 'Tournament',
                id: args?.id,
              });
            },
          },
          posts: {
            keyArgs: false,
            merge: mergePaginatedCollections,
          },
          post: {
            read(_, { canRead, toReference, args }) {
              const postTypes = [
                'RegularPost',
                'RePost',
                'StreamPost',
                'TournamentPost',
              ];

              for (const typename of postTypes) {
                const reference = toReference({
                  __typename: typename,
                  id: args?.id,
                });
                if (canRead(reference)) {
                  return reference;
                }
              }
              return undefined;
            },
          },
          streamsAll: {
            keyArgs: ['streamCategory', 'custom', 'pricingType', 'streamState'],
            merge: mergePaginatedCollections,
          },
          streamsCreated: {
            keyArgs: [
              'streamCategory',
              'custom',
              'pricingType',
              'streamState',
              'streamType',
            ],
            merge: mergePaginatedCollections,
          },
          streamsFollowing: {
            keyArgs: [
              'streamCategory',
              'custom',
              'pricingType',
              'streamState',
              'streamType',
            ],
            merge: mergePaginatedCollections,
          },
          streamPosts: {
            keyArgs: ['id', 'matchFilter'],
            merge: mergePaginatedCollections,
          },
          enterpriseStreamPosts: {
            keyArgs: ['id', 'leafAz', 'idempotencyKey'],
            merge: mergePaginatedCollections,
          },
          streamPackages: {
            keyArgs: ['queryType'],
            merge: mergePaginatedCollections,
          },
          userStreams: {
            keyArgs: ['username'],
            merge: mergePaginatedCollections,
          },
          streamUsers: {
            keyArgs: ['type', 'streamId'],
          },
          typeahead: {
            keyArgs: ['input', ['text', 'type']],
            merge(existing, incoming: TypeaheadResult, { args }) {
              const itemsKey =
                args?.input?.type?.[0] === 'Stream' ? 'streams' : 'users';
              const existedItems = existing ? [...existing[itemsKey]] : [];
              const incomingItems = incoming?.[itemsKey]?.filter(
                (item) => !existedItems.includes(item),
              );

              if (incomingItems) {
                const {
                  input: { offset },
                } = args as QueryTypeaheadArgs;

                for (let i = 0; i < incomingItems.length; ++i) {
                  existedItems[(offset || 0) + i] = incomingItems[i];
                }
              }

              return {
                ...incoming,
                [itemsKey]: existedItems,
                offset: existing?.offset === null ? null : incoming?.offset,
              };
            },
          },
          customCollectionPosts: {
            keyArgs: ['collectionId', 'matchFilter'],
            merge: mergePaginatedCollections,
          },
          search: {
            keyArgs: [
              'input',
              [
                'type',
                'text',
                'filters',
                ['type', 'tournament', ['category', 'state']],
                ['filters', ['stream', ['streamCategory', 'custom']]],
              ],
            ],
            merge(existing, incoming, { args }) {
              const merged = existing ? existing.items.slice(0) : [];

              if (incoming?.items) {
                const {
                  input: { offset = 0 },
                } = args as QuerySearchArgs;

                for (let i = 0; i < incoming.items.length; ++i) {
                  merged[offset + i] = incoming.items[i];
                }
              }

              return {
                ...incoming,
                offset: existing?.offset === null ? null : incoming?.offset,
                items: merged,
              };
            },
          },
          searchStreamPosts: {
            keyArgs: ['input', ['text', 'streamId', 'fromDate', 'toDate']],
            merge(existing, incoming, { args }) {
              const merged = existing ? existing.items.slice(0) : [];

              if (incoming?.items) {
                const {
                  input: { offset = 0 },
                } = args as QuerySearchStreamPostsArgs;

                for (let i = 0; i < incoming.items.length; ++i) {
                  merged[offset + i] = incoming.items[i];
                }
              }

              return {
                ...incoming,
                offset: existing?.offset === null ? null : incoming?.offset,
                items: merged,
              };
            },
          },
          allStreamsPostsSearch: {
            keyArgs: ['input', ['streamIds', 'text', 'fromDate', 'toDate']],
            merge(existing, incoming, { args }) {
              const merged = existing ? existing.items.slice(0) : [];

              if (incoming?.items) {
                const {
                  input: { offset = 0 },
                } = args as QueryAllStreamsPostsSearchArgs;

                for (let i = 0; i < incoming.items.length; ++i) {
                  merged[offset + i] = incoming.items[i];
                }
              }

              return {
                ...incoming,
                offset: existing?.offset === null ? null : incoming?.offset,
                items: merged,
              };
            },
          },
          mySources: {
            keyArgs: ['query', 'tag'],
            merge(existing, incoming, { args }) {
              const { cursor } = (args ?? {}) as QueryMySourcesArgs;
              if (!cursor || !existing) {
                return incoming;
              }

              // apollo can write the same page multiple times (see mergePaginatedCollections)
              if (incoming.nextCursor === existing.nextCursor) {
                return existing;
              }

              return {
                ...incoming,
                items: [...existing.items, ...incoming.items],
              };
            },
          },
          polymatcherCandidates: {
            keyArgs: [
              'input',
              [
                'newsText',
                'unitText',
                'marketText',
                'window',
                'inferenceFired',
                'pipelineFired',
                'sort',
                'previewLength',
                'pageSize',
              ],
            ],
            merge: mergeCursorPages,
          },
          enrichmentInvocations: {
            keyArgs: ['sourceId', 'service', 'outcome', 'from', 'to'],
            merge(existing, incoming, { args }) {
              const { after } = (args ?? {}) as QueryEnrichmentInvocationsArgs;
              if (!after || !existing) {
                return incoming;
              }

              if (incoming.nextCursor === existing.nextCursor) {
                return existing;
              }

              return {
                ...incoming,
                items: [...existing.items, ...incoming.items],
              };
            },
          },
          notifications: {
            keyArgs: ['read'],
            merge: mergePaginatedCollections,
          },
          userStreamInvites: {
            merge: mergePaginatedCollections,
          },
          streamInvites: {
            keyArgs: ['streamId'],
          },
          followersByName: {
            keyArgs: ['isInfluential', 'username', 'limit'],
            merge: mergePaginatedCollections,
          },
          followingByName: {
            keyArgs: ['username'],
            merge: mergePaginatedCollections,
          },
          discussionFeed: {
            keyArgs: ['discussionContextId'],
            merge: mergePaginatedCollections,
          },
          organizationUsers: {
            keyArgs: ['id'],
            merge: mergePaginatedCollections,
          },
          organizations: {
            merge: mergePaginatedCollections,
          },
          getUsers: {
            keyArgs: ['input', ['searchString', 'hasStripe']],
            merge: mergePaginatedCollections,
          },
          voxFullAccessRequests: {
            keyArgs: ['status'],
            merge: mergePaginatedCollections,
          },
          voxHistory: {
            keyArgs: ['input', ['event']],
            merge: mergePaginatedCollections,
          },
          voxTextSearch: {
            keyArgs: [
              'input',
              [
                'query',
                'order',
                'messageType',
                'excludeForwards',
                'onlyWithMedia',
                'fromDate',
                'toDate',
                'excludeGroupsIds',
              ],
            ],
            merge(existing, incoming, { args }) {
              if (!existing) return incoming;

              if (args?.input?.offset === 0) {
                return incoming;
              }

              return {
                ...incoming,
                messages: [
                  ...(existing.messages || []),
                  ...(incoming.messages || []),
                ],
              };
            },
          },
        },
      },
    },
  });
